import { useThreadStateRealtime } from '@/components/activity/useThreadStateRealtime';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowLeft } from 'lucide-react';
import { AssistantPanel } from '@/components/layout/AssistantPanel';
import { useAppAssistant } from '@/lib/app-assistant-context';
import { SubjectComposer } from '@/components/activity/SubjectComposer';
import { getActivitySubjectDetail } from '@/lib/activity-feed';
import { getSubjectWorkActions } from '@/lib/work-actions';
import { WorkActionCard } from './WorkActionCard';
import type { WorkActionPresentation } from '@shared/types/work-action';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';

export const WorkConversationPanel = () => {
  const { workFocus, leaveWork, closeAssistant } = useAppAssistant();
  const [actions, setActions] = useState<WorkActionPresentation[]>([]);
  const [revision, setRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  useThreadStateRealtime({ enabled: Boolean(workFocus), threadId: workFocus?.threadId, onRefresh: () => setRevision(v => v + 1) });
  const focused = actions.find(action => action.context.subjectId === workFocus?.subjectId && (
    workFocus?.topicId ? action.context.topicId === workFocus.topicId
      : Boolean(workFocus?.sourceRunId && workFocus.sourceActionId)
        && action.context.sourceRunId === workFocus?.sourceRunId
        && action.context.sourceActionId === workFocus?.sourceActionId
  ));
  const context = workFocus ? { ...workFocus, ...focused?.context } : null;
  const status = context?.preparation?.status;
  const preparing = status === 'queued' || status === 'running';
  useEffect(() => {
    if (!workFocus || workFocus.flowResultId) return;
    let alive = true;
    let request = 0;
    const refresh = () => {
      const current = ++request;
      void getActivitySubjectDetail({ subjectId: workFocus.subjectId, subjectKey: workFocus.subjectKey, subjectKind: workFocus.subjectKind, subjectLabel: workFocus.subjectLabel })
        .then(({ detail }) => { if (alive && request === current) { setActions(detail ? getSubjectWorkActions(detail) : []); setError(null); } })
        .catch(() => { if (alive && request === current) setError('Impossible de recharger ce travail. Votre demande est conservée.'); });
    };
    refresh();
    // Realtime remains primary; this bounded check covers missed notifications.
    let checks = 0;
    const timer = preparing ? window.setInterval(() => { if (++checks <= 60) refresh(); }, 5000) : undefined;
    window.addEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    window.addEventListener('online', refresh);
    return () => {
      alive = false;
      window.clearInterval(timer);
      window.removeEventListener(ACCOUNT_TASK_COMPLETED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('online', refresh);
    };
  }, [workFocus, revision, preparing]);
  if (!workFocus || !context) return null;
  return <AssistantPanel label="Travail avec Domino">
    <header className="flex items-start gap-3 border-b border-domino-cardBorder p-4">
      <button aria-label="Revenir à la conversation générale" onClick={leaveWork} className="p-2"><ArrowLeft className="h-4 w-4" /></button>
      <Link to={`/threads/${workFocus.subjectId}`} className="min-w-0 flex-1 font-semibold text-domino-aubergine">{workFocus.subjectLabel}</Link>
      <button aria-label="Réduire la conversation" onClick={() => closeAssistant()} className="p-2"><X className="h-4 w-4" /></button>
    </header>
    <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
      {workFocus.flowResultId ? <p className="font-semibold text-domino-ink">{workFocus.title}</p> : focused && <WorkActionCard work={focused} compact hideControls />}
      {error && !workFocus.flowResultId && <p role="alert" className="text-sm text-red-700">{error}</p>}
      <SubjectComposer
        key={`${workFocus.subjectId}:${workFocus.flowResultId ? `flow:${workFocus.flowResultId}` : workFocus.topicId}`}
        subjectKey={workFocus.subjectKey}
        subjectKind={workFocus.subjectKind}
        subjectLabel={workFocus.subjectLabel}
        workFocus={context}
        continueFromResult={Boolean(context.resultId || context.flowResultId) || focused?.command.kind === 'resume'}
      />
    </div>
  </AssistantPanel>;
};
